import { useState, useEffect, useCallback, useRef } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import UsageBadge from '@/components/UsageBadge';
import useUsageSummary from '@/hooks/useUsageSummary';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Megaphone, Plus, Play, Pause, Trash2, Eye, Send, Users, FileText, Settings as SettingsIcon, Shield, ChevronLeft, ChevronRight, Loader2, Info, CheckCircle2, Clock, AlertTriangle, BarChart3, MessageCircle, RefreshCw, Upload, FileSpreadsheet, Filter, Calendar as CalendarIcon, Download, Video } from 'lucide-react';
import * as XLSX from 'xlsx';
import HelpTip from '@/components/HelpTip';
import {
  TEMPLATE_FIELD_SAMPLES, dummyValueFor, BASE_COLUMN_ALIAS_SET,
  buildDynamicColumns, downloadCampaignTemplate, CHANNEL_TO_PROVIDER,
} from './mass_campaigns/helpers';

// Nota: `extractPlaceholderIndexes`, `resolveParamsForRecipient` y
// `renderTemplatePreview` quedan en este archivo por tener semantica especifica
// acoplada al flujo del wizard. Pueden extraerse mas adelante.

const STEPS = [
  { id: 'info', label: 'Informacion Basica', icon: Info, tip: 'Define el nombre, canal y tipo de envio.' },
  { id: 'content', label: 'Contenido', icon: FileText, tip: 'Selecciona la plantilla HSM y mapea sus parametros.' },
  { id: 'audience', label: 'Audiencia', icon: Users, tip: 'Selecciona a quien enviar los mensajes.' },
  { id: 'config', label: 'Configuracion', icon: SettingsIcon, tip: 'Ajusta la velocidad de envio.' },
  { id: 'antiban', label: 'Anti-Baneo', icon: Shield, tip: 'Protege tus cuentas con limites inteligentes.' },
  { id: 'review', label: 'Revisar', icon: Eye, tip: 'Revisa todo antes de enviar.' },
];

const CHANNEL_INFO = {
  waba: { name: 'WhatsApp Business API (Oficial)', desc: 'Alto volumen, templates, confirmaciones de lectura. Ideal para envios masivos con compliance.', color: 'emerald' },
  twilio: { name: 'Twilio WhatsApp', desc: 'Envio via Twilio. Requiere cuenta con WhatsApp habilitado.', color: 'red' },
  gupshup: { name: 'Gupshup WhatsApp', desc: 'Envio via Gupshup API. Alto volumen y buen soporte.', color: 'green' },
};

// Providers validos para template-only send (backend Prompt 2).
// Evolution NO aparece: no soporta templates HSM en modo QR/Baileys.
const PROVIDERS = {
  gupshup: { label: 'Gupshup WhatsApp', desc: 'Envío vía Gupshup API. Alto volumen.', color: 'green' },
  twilio: { label: 'Twilio WhatsApp', desc: 'Envío vía Twilio Messaging API.', color: 'red' },
  meta_cloud: { label: 'Meta WhatsApp Cloud API', desc: 'Envío vía Meta Cloud API (WABA oficial).', color: 'emerald' },
};

// Mapper canal_legacy -> provider nuevo (importado desde helpers).

// Detecta placeholders {{1}}, {{2}}, ... del body del template y retorna indexes únicos ordenados.
function extractPlaceholderIndexes(body) {
  const set = new Set();
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m;
  while ((m = re.exec(String(body || ''))) !== null) {
    const n = parseInt(m[1], 10);
    if (!Number.isNaN(n)) set.add(n);
  }
  return Array.from(set).sort((a, b) => a - b);
}

// Resuelve template_params contra un recipient (mismo contrato que backend).
function resolveParamsForRecipient(templateParams, recipient) {
  const out = [];
  const rec = recipient || {};
  const custom = (rec.custom_fields && typeof rec.custom_fields === 'object') ? rec.custom_fields : {};
  for (const p of templateParams || []) {
    if (!p) { out.push(''); continue; }
    if (p.type === 'fixed') {
      out.push(String(p.value || ''));
    } else if (p.type === 'col_excel') {
      const colKey = `{{${p.index}}}`;
      if (Object.prototype.hasOwnProperty.call(rec, colKey)) {
        out.push(String(rec[colKey] ?? ''));
      } else if (Object.prototype.hasOwnProperty.call(custom, colKey)) {
        out.push(String(custom[colKey] ?? ''));
      } else {
        out.push('');
      }
    } else {
      const fn = p.field_name || '';
      if (Object.prototype.hasOwnProperty.call(rec, fn)) {
        out.push(String(rec[fn] ?? ''));
      } else if (Object.prototype.hasOwnProperty.call(custom, fn)) {
        out.push(String(custom[fn] ?? ''));
      } else {
        out.push('');
      }
    }
  }
  return out;
}

// Renderiza preview: reemplaza {{N}} del body con params resueltos.
function renderTemplatePreview(body, resolved) {
  let text = String(body || '');
  resolved.forEach((val, i) => {
    const re = new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g');
    text = text.replace(re, String(val ?? ''));
  });
  return text;
}

// Extrae campos candidatos del recipient (name/phone/email + top-level + custom_fields).
function extractRecipientFields(recipients) {
  const fields = new Set(['name', 'phone', 'email']);
  (recipients || []).slice(0, 20).forEach(r => {
    if (!r || typeof r !== 'object') return;
    Object.keys(r).forEach(k => {
      if (k === 'custom_fields' || k === '_id') return;
      if (typeof r[k] !== 'object' || r[k] === null) fields.add(k);
    });
    const cf = r.custom_fields;
    if (cf && typeof cf === 'object') Object.keys(cf).forEach(k => fields.add(k));
  });
  return Array.from(fields);
}

function DebugInfoPanel({ campaign }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const debugJson = {
    generated_at: new Date().toISOString(),
    env: window.location.hostname.includes('lab.') ? 'staging' : 'prod',
    db: window.location.hostname.includes('lab.') ? 'dealider_staging_db' : 'dealider_db',
    company: {
      id: user?.company_id,
      name: user?.company_name,
    },
    campaign: {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      provider: campaign.provider,
      template_id: campaign.template_id,
      template_name: campaign.template_name,
      total_recipients: campaign.total_recipients,
      total_intents: campaign.total_intents,
      sent_count: campaign.sent_count,
      failed_count: campaign.failed_count,
      created_at: campaign.created_at,
      started_at: campaign.started_at,
      completed_at: campaign.completed_at,
      schedule_type: campaign.schedule_type,
      scheduled_at: campaign.scheduled_at,
    },
    waba: {
      account_id: campaign.waba_account_id,
      provider: campaign.provider,
    },
    rate_config: campaign.rate_config || null,
    anti_ban: campaign.anti_ban || null,
    template_params: campaign.template_params || [],
    stats: {
      sent: campaign.sent_count,
      failed: campaign.failed_count,
      delivered: campaign.delivered_count,
      read: campaign.read_count,
      blocked_by_meta: campaign.blocked_by_meta_count,
      pending: campaign.pending_count,
    },
    codex_query_hint:
      `En message_intents donde campaign_id="${campaign.id}": ` +
      `agrupa por status y cuenta. ` +
      `Para rate: agrupa sent_at por minuto y cuenta.`,
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(debugJson, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3" data-testid="debug-info-panel">
      <div className="flex justify-between items-center">
        <p className="text-xs text-white/50">
          Copia este JSON para diagnóstico con Codex
        </p>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-xs text-white transition-colors"
          data-testid="debug-info-copy-btn"
        >
          {copied ? (
            <>
              <CheckCircle2 size={12} />
              Copiado
            </>
          ) : (
            <>
              <FileText size={12} />
              Copiar JSON
            </>
          )}
        </button>
      </div>
      <pre className="bg-black/40 rounded-lg p-4 text-[11px] text-green-400 overflow-x-auto whitespace-pre-wrap font-mono border border-white/5">
        {JSON.stringify(debugJson, null, 2)}
      </pre>
    </div>
  );
}

export default function MassCampaignsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const { data: usage, reload: reloadUsage } = useUsageSummary();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [segments, setSegments] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [audienceMode, setAudienceMode] = useState('segments');
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [optOutWarning, setOptOutWarning] = useState(null);
  const [debugCampaign, setDebugCampaign] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [pipelineDateFrom, setPipelineDateFrom] = useState('');
  const [pipelineDateTo, setPipelineDateTo] = useState('');
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '', description: '', campaign_mode: 'whatsapp',
    crm_campaign_id: '',
    provider: 'gupshup', channel_type: 'gupshup',
    schedule_type: 'immediate', scheduled_at: '',
    segment_id: '', segment_name: '', recipients: [],
    message_body: '', message_variations: [],
    template_id: '', template_name: '', template_params: [],
    media_url: '',
    call_prompt: '', ai_greeting: '', ai_full_prompt: '', ai_call_objectives: '', ai_conversation_script: '',
    ai_db_permissions: { inventory: true, prices: true, appointments: true, customer_history: false },
    ai_metrics_config: { track_duration: true, track_outcome: true, track_intent: true, track_followups: true },
    voice_profile: 'female_es',
    rate_config: { msgs_per_minute: 1000, msgs_per_hour: 10000, delay_between: 0.05 },
    anti_ban: {
      enabled: true, mode: 'moderate', business_hours: false, respect_weekends: false,
      randomize_delays: true, delay_min: 3, delay_max: 15,
      account_rotation: true, cooldown_minutes: 30, message_variation: false,
    },
  });

  // Templates state (filtrado por provider + status=APPROVED)
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesSyncing, setTemplatesSyncing] = useState(false);

  const loadTemplates = useCallback(async (provider) => {
    if (!provider) { setTemplates([]); return; }
    setTemplatesLoading(true);
    try {
      if (provider === 'meta_cloud') {
        // wa_templates.py GET /whatsapp/templates sincroniza Cloud y retorna la lista cacheada.
        const r = await api.get('/whatsapp/templates');
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setTemplates(list.filter(t => String(t.status || '').toUpperCase() === 'APPROVED'));
      } else {
        const r = await api.get('/whatsapp/templates/all', {
          params: { provider, status: 'APPROVED', page_size: 500 },
        });
        setTemplates(r.data?.items || []);
      }
    } catch {
      setTemplates([]);
    } finally { setTemplatesLoading(false); }
  }, []);

  const syncMetaTemplates = async () => {
    setTemplatesSyncing(true);
    try {
      await api.get('/whatsapp/templates');
      toast.success('Plantillas sincronizadas');
      await loadTemplates('meta_cloud');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al sincronizar');
    } finally { setTemplatesSyncing(false); }
  };

  const syncGupshupTemplates = async () => {
    setTemplatesSyncing(true);
    try {
      const cfgResp = await api.get('/whatsapp/gupshup/numbers');
      const configs = cfgResp.data?.numbers || cfgResp.data?.items || [];
      if (!configs.length) {
        toast.error('No hay configuraciones Gupshup activas');
        return;
      }
      let total = 0;
      for (const cfg of configs) {
        if (!cfg?.id) continue;
        try {
          const fr = await api.get('/whatsapp/gupshup/templates', { params: { config_id: cfg.id } });
          const tpls = fr.data?.templates || [];
          if (tpls.length) {
            await api.post('/whatsapp/gupshup/templates', { config_id: cfg.id, templates: tpls });
            total += tpls.length;
          }
        } catch { /* continue con siguiente config */ }
      }
      toast.success(`Plantillas sincronizadas (${total})`);
      await loadTemplates('gupshup');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al sincronizar');
    } finally { setTemplatesSyncing(false); }
  };

  const syncTwilioTemplates = async () => {
    setTemplatesSyncing(true);
    try {
      const fr = await api.get('/whatsapp/twilio/templates');
      const tpls = fr.data?.templates || fr.data?.items || [];
      if (tpls.length) {
        await api.post('/whatsapp/twilio/templates/save', { templates: tpls });
      }
      toast.success(`Plantillas sincronizadas (${tpls.length})`);
      await loadTemplates('twilio');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al sincronizar');
    } finally { setTemplatesSyncing(false); }
  };

  const handleSyncTemplates = () => {
    if (form.provider === 'meta_cloud') return syncMetaTemplates();
    if (form.provider === 'gupshup')    return syncGupshupTemplates();
    if (form.provider === 'twilio')     return syncTwilioTemplates();
  };

  // --- Detail panel state ---
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [intents, setIntents] = useState([]);
  const [intentsLoading, setIntentsLoading] = useState(false);

  // --- Test send dialog ---
  const [testSendOpen, setTestSendOpen] = useState(false);

  // --- Active providers (Mejora 2) ---
  const [activeProviders, setActiveProviders] = useState({ gupshup: true, twilio: true, meta_cloud: true });
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [crmCampaigns, setCrmCampaigns] = useState([]);

  const loadActiveProviders = useCallback(async () => {
    try {
      const r = await api.get('/whatsapp/providers/active');
      const data = r.data || {};
      // Normalizar a booleans estrictos para evitar valores truthy ambiguos (null, "true", etc).
      setActiveProviders({
        gupshup: data.gupshup === true,
        twilio: data.twilio === true,
        meta_cloud: data.meta_cloud === true,
      });
    } catch (err) {
      // Fallback SEGURO: ninguno activo. El wizard mostrara el empty-state guiando
      // al admin a configurar un proveedor. Nunca asumir "todos activos".
      console.error('[MassCampaigns] providers/active failed:', err);
      setActiveProviders({ gupshup: false, twilio: false, meta_cloud: false });
    } finally { setProvidersLoaded(true); }
  }, []);

  useEffect(() => {
    if (wizard) loadActiveProviders();
  }, [wizard, loadActiveProviders]);

  const loadCrmCampaigns = useCallback(async () => {
    try {
      const r = await api.get('/campaigns', { params: { channel: 'whatsapp' } });
      setCrmCampaigns(Array.isArray(r.data) ? r.data : []);
    } catch {
      setCrmCampaigns([]);
    }
  }, []);

  useEffect(() => {
    if (wizard) loadCrmCampaigns();
  }, [wizard, loadCrmCampaigns]);

  // Auto-seleccionar provider si el tenant solo tiene uno activo.
  useEffect(() => {
    if (!providersLoaded || !wizard) return;
    const actives = Object.entries(activeProviders).filter(([k, v]) => v && PROVIDERS[k]).map(([k]) => k);
    if (actives.length === 1 && form.provider !== actives[0]) {
      setForm(f => ({ ...f, provider: actives[0], channel_type: actives[0], template_id: '', template_name: '', template_params: [] }));
    } else if (actives.length >= 2 && !actives.includes(form.provider)) {
      // Si el default gupshup no está entre los activos, elegir el primero activo.
      setForm(f => ({ ...f, provider: actives[0], channel_type: actives[0], template_id: '', template_name: '', template_params: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providersLoaded, activeProviders, wizard]);

  const openDetail = async (campaign) => {
    setDetailCampaign(campaign);
    setIntents([]);
    setIntentsLoading(true);
    try {
      const r = await api.get(`/mass-campaigns/${campaign.id}/intents`);
      setIntents(r.data?.intents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar detalle');
    } finally { setIntentsLoading(false); }
  };

  useEffect(() => {
    if (wizard && form.campaign_mode === 'whatsapp' && form.provider) {
      loadTemplates(form.provider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, form.provider, form.campaign_mode]);

  const loadCampaigns = useCallback(async () => {
    try {
      const r = await api.get('/mass-campaigns');
      setCampaigns(r.data);
    } catch { /* ok */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadSegments = async () => {
    try { const r = await api.get('/mass-campaigns/segments/list'); setSegments(r.data); } catch { /* ok */ }
  };

  const loadPipelineStages = async () => {
    try { const r = await api.get('/pipeline'); setPipelineStages(r.data || []); } catch { /* ok */ }
  };

  const loadRecipients = async (segId) => {
    try {
      const r = await api.get(`/mass-campaigns/segments/${segId}/recipients`);
      setRecipients(r.data);
      setForm(f => ({ ...f, recipients: r.data, segment_id: segId }));
    } catch { toast.error('Error cargando destinatarios'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOptOutWarning(null);
    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/mass-campaigns/upload-contacts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedFile({ name: file.name, total: r.data.total });
      setRecipients(r.data.contacts);
      setForm(f => ({ ...f, recipients: r.data.contacts, segment_id: '', segment_name: `Archivo: ${file.name}` }));
      if (r.data.opted_out_count > 0) {
        setOptOutWarning({
          count: r.data.opted_out_count,
          total: r.data.total,
          phones: r.data.opted_out_phones || [],
        });
      } else {
        setOptOutWarning(null);
      }
      toast.success(`${r.data.total} contactos cargados de ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar archivo');
    } finally {
      setFileUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const loadPipelineLeads = async () => {
    if (!selectedStages.length) { toast.error('Selecciona al menos una etapa'); return; }
    setPipelineLoading(true);
    try {
      const r = await api.post('/mass-campaigns/pipeline-leads', {
        stage_ids: selectedStages,
        date_from: pipelineDateFrom || undefined,
        date_to: pipelineDateTo || undefined,
      });
      setRecipients(r.data.contacts);
      setForm(f => ({ ...f, recipients: r.data.contacts, segment_id: '', segment_name: `Pipeline (${selectedStages.length} etapas)` }));
      toast.success(`${r.data.total} contactos encontrados del pipeline`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al buscar leads');
    } finally { setPipelineLoading(false); }
  };

  const openWizard = () => {
    setStep(0);
    setForm({
      name: '', description: '', campaign_mode: 'whatsapp',
      provider: 'gupshup', channel_type: 'gupshup',
      schedule_type: 'immediate', scheduled_at: '',
      segment_id: '', segment_name: '', recipients: [],
      message_body: '', message_variations: [],
      template_id: '', template_name: '', template_params: [],
      media_url: '',
      call_prompt: '', ai_greeting: '', ai_full_prompt: '', ai_call_objectives: '', ai_conversation_script: '',
      ai_db_permissions: { inventory: true, prices: true, appointments: true, customer_history: false },
      ai_metrics_config: { track_duration: true, track_outcome: true, track_intent: true, track_followups: true },
      voice_profile: 'female_es',
      rate_config: { msgs_per_minute: 1000, msgs_per_hour: 10000, delay_between: 0.05 },
      anti_ban: { enabled: true, mode: 'moderate', business_hours: false, respect_weekends: false, randomize_delays: true, delay_min: 3, delay_max: 15, account_rotation: true, cooldown_minutes: 30, message_variation: false },
    });
    setTemplates([]);
    setRecipients([]);
    setUploadedFile(null);
    setAudienceMode('segments');
    setSelectedStages([]);
    setPipelineDateFrom('');
    setPipelineDateTo('');
    loadSegments();
    loadPipelineStages();
    setWizard(true);
  };

  const saveDraft = async () => {
    if (!form.name) { toast.error('Nombre de campana requerido'); return; }
    setSaving(true);
    try {
      const r = await api.post('/mass-campaigns', { ...form, status: 'draft' });
      toast.success('Borrador guardado');
      setWizard(false);
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleExportOptOuts = async () => {
    try {
      const response = await api.get('/mass-campaigns/opt-outs/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bajas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al exportar bajas');
    }
  };

  // Derivados para validación/preview del template.
  const selectedTemplate = form.template_id ? (templates.find(t => t.id === form.template_id) || null) : null;
  const placeholderIndexes = extractPlaceholderIndexes(selectedTemplate?.body || '');
  const firstRecipient = form.recipients?.[0] || null;
  const resolvedParamsPreview = resolveParamsForRecipient(form.template_params, firstRecipient);
  const templatePreviewText = selectedTemplate
    ? renderTemplatePreview(selectedTemplate.body || '', resolvedParamsPreview)
    : '';

  const templateParamsValid = form.campaign_mode !== 'whatsapp' || placeholderIndexes.every(idx => {
    const p = (form.template_params || []).find(x => x.index === idx);
    if (!p) return false;
    if (p.type === 'fixed') return String(p.value || '').trim().length > 0;
    if (p.type === 'field') return Boolean(p.field_name);
    if (p.type === 'col_excel') return true;
    return false;
  });

  // Columnas dinamicas derivadas de los params del template actual.
  // Lee form.template_params en vivo y construye: [telefono, nombre, email, ...extras].
  // type='field'  -> columna field_name (si no es alias de base)
  // type='col_excel' -> columna literal `{{N}}`
  const extraColumnsSpec = buildDynamicColumns(form.template_params || []);
  const dynamicTemplateColumns = ['telefono', 'nombre', 'email', ...extraColumnsSpec.map(e => e.key)];
  const handleDownloadTemplate = () => {
    downloadCampaignTemplate({
      templateName: form.template_name || '',
      templateParams: form.template_params || [],
    });
  };

  const sendDisabledReason = (() => {
    if (!form.name?.trim()) return 'Falta nombre de campaña';
    if (!form.recipients?.length) return 'Sin destinatarios';
    if (form.campaign_mode === 'whatsapp') {
      if (!form.provider) return 'Selecciona un proveedor';
      if (!form.template_id) return 'Selecciona una plantilla';
      if (!templateParamsValid) {
        // Identificar cuáles parámetros faltan específicamente
        const missingParams = placeholderIndexes.filter(idx => {
          const p = (form.template_params || []).find(x => x.index === idx);
          if (!p) return true;
          if (p.type === 'fixed') return !String(p.value || '').trim();
          if (p.type === 'field') return !p.field_name;
          return false;
        });
        if (missingParams.length === 1) {
          return `Falta configurar el parámetro {{${missingParams[0]}}}`;
        }
        if (missingParams.length > 1) {
          return `Faltan configurar los parámetros: ${missingParams.map(i => `{{${i}}}`).join(', ')}`;
        }
        return 'Completa todos los parámetros del template';
      }
    }
    if (form.campaign_mode === 'ai_calls' && !form.call_prompt) return 'Falta prompt de llamada';
    return '';
  })();

  const sendCampaign = async () => {
    if (sendDisabledReason) { toast.error(sendDisabledReason); return; }
    if (!form.crm_campaign_id) {
      toast.error('Selecciona una campaña CRM antes de enviar');
      return;
    }
    setSending(true);
    try {
      if (form.schedule_type === 'scheduled') {
        // Programar: solo guarda en Mongo con status='scheduled'. El dispatcher del
        // backend la ejecutara cuando scheduled_at_utc <= now (cada 60s).
        if (!form.scheduled_at) {
          toast.error('Selecciona fecha y hora de envio');
          setSending(false);
          return;
        }
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
        const payload = {
          ...form,
          status: 'scheduled',
          user_timezone: tz,
          message_body: form.campaign_mode === 'whatsapp' ? templatePreviewText : form.message_body,
        };
        const programmedRes = await api.post('/mass-campaigns', payload);
        const pti = programmedRes.data?.truncate_info;
        if (pti?.truncated) {
          toast.warning(`Tu plan permite ${pti.cap} envíos/mes (usados ${pti.used}). Se programaron ${pti.kept} destinatarios y ${pti.dropped} quedaron fuera por límite mensual.`, { duration: 9000 });
        }
        toast.success(`Campaña programada para ${form.scheduled_at} (${tz}). Se enviara automaticamente.`);
      } else {
        // Envio inmediato: guardar + disparar /send en el momento.
        const payload = {
          ...form,
          status: 'ready',
          message_body: form.campaign_mode === 'whatsapp' ? templatePreviewText : form.message_body,
        };
        const r = await api.post('/mass-campaigns', payload);
        const ti = r.data?.truncate_info;
        if (ti?.truncated) {
          toast.warning(`Tu plan permite ${ti.cap} envíos/mes (usados ${ti.used}). Se enviarán ${ti.kept} destinatarios y ${ti.dropped} quedaron fuera por límite mensual. Considera upgradear.`, { duration: 9000 });
        }
        const res = await api.post(`/mass-campaigns/${r.data.id}/send`);
        toast.success(`Enviando a ${res.data.total} destinatarios...`);
        reloadUsage();
      }
      setWizard(false);
      loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  const deleteCampaign = async (id) => {
    try { await api.delete(`/mass-campaigns/${id}`); toast.success('Eliminada'); loadCampaigns(); }
    catch { toast.error('Error'); }
  };

  const pauseCampaign = async (id) => {
    try { await api.post(`/mass-campaigns/${id}/pause`); toast.success('Pausada'); loadCampaigns(); }
    catch { toast.error('Error'); }
  };

  const setAntiBanMode = (mode) => {
    const presets = {
      conservative: { delay_min: 8, delay_max: 25, cooldown_minutes: 60, business_hours: true, respect_weekends: true, account_rotation: true, message_variation: true },
      moderate: { delay_min: 3, delay_max: 15, cooldown_minutes: 30, business_hours: false, respect_weekends: false, account_rotation: true, message_variation: true },
      aggressive: { delay_min: 1, delay_max: 5, cooldown_minutes: 10, business_hours: false, respect_weekends: false, account_rotation: true, message_variation: false },
    };
    setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, mode, ...(presets[mode] || {}) } }));
  };

  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="space-y-6" data-testid="mass-campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2"><Megaphone className="w-6 h-6 text-orange-400" /> WhatsApp / Voice Blaster <HelpTip title="WhatsApp / Voice Blaster" content="WhatsApp Blaster: plantillas masivas. Voice Blaster: la IA llama al lead (motor A/B de voz), no transfiere a un vendedor. Cada respuesta crea o enriquece el lead." /></h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-xs text-muted-foreground">Envío masivo de WhatsApp o llamadas IA, con anti-baneo</p>
            <UsageBadge status={usage?.mass_campaign_sends} label="Envíos este mes" testId="mass-sends-usage-badge" />
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportOptOuts}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors text-xs"
              data-testid="export-opt-outs-btn"
            >
              <span>⬇️</span>
              Exportar Bajas
            </button>
            <Button onClick={openWizard} className="bg-orange-600 hover:bg-orange-500 text-xs" data-testid="new-campaign-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Nueva Campana
            </Button>
          </div>
        )}
      </div>

      {/* Campaign List */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-orange-400 animate-spin" /></div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20" data-testid="no-campaigns">
          <Megaphone className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">No hay campañas. Crea tu primer WhatsApp o Voice Blaster.</p>
          <p className="text-xs text-muted-foreground/60 mt-2 max-w-md mx-auto">Solo funciona con canales WABA (API oficial), Twilio o Gupshup. El modo QR no soporta envios masivos.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="campaigns-list">
          {campaigns.map(c => (
            <CampaignRow key={c.id} campaign={c} onDelete={() => deleteCampaign(c.id)} onPause={() => pauseCampaign(c.id)} onOpen={() => openDetail(c)} onReload={loadCampaigns} onDebug={isAdmin ? () => setDebugCampaign(c) : null} />
          ))}
        </div>
      )}

      {/* Wizard Dialog */}
      <Dialog open={wizard} onOpenChange={v => { if (!sending) setWizard(v); }}>
        <DialogContent className="bg-[#0d1117] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {/* Wizard Header */}
          <div className="p-5 border-b border-white/5">
            <h2 className="text-base font-heading font-bold text-white">Crear Campana</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">Paso {step + 1} de {STEPS.length}: {STEPS[step].label}</p>
            <div className="h-1.5 rounded-full bg-black/30 mt-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            {/* Step indicators */}
            <div className="flex gap-1 mt-3">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => setStep(i)} className={`flex-1 p-2 rounded-lg text-center transition-all text-[9px] ${i === step ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : i < step ? 'bg-emerald-500/10 text-emerald-400' : 'bg-black/20 text-muted-foreground'}`} data-testid={`wizard-step-${s.id}`}>
                    <Icon className="w-3.5 h-3.5 mx-auto mb-0.5" />{s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-5">
            {/* Contextual help */}
            <div className="mb-4 p-3 rounded-lg bg-orange-500/5 border border-orange-500/10 flex items-start gap-2">
              <Info className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-orange-300/70">{STEPS[step].tip}</p>
            </div>

            {/* Step 1: Info Basica */}
            {step === 0 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-white/60">Nombre de la Campana *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Promo Enero 2026" className="bg-black/20 border-white/10 text-sm" data-testid="campaign-name" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Descripcion</Label>
                  <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe tu campana" className="bg-black/20 border-white/10 text-sm min-h-[60px]" data-testid="campaign-desc" />
                </div>
                <div>
                  <Label className="text-xs text-white/60 mb-2 block">Canal de Envio</Label>
                  <p className="text-[10px] text-muted-foreground mb-2">Solo WABA, Twilio y Gupshup soportan envios masivos. El modo QR no es compatible.</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[{id:'whatsapp', label:'WhatsApp Blaster', desc:'Plantillas masivas por Gupshup, Twilio o Meta'}, {id:'ai_calls', label:'Voice Blaster', desc:'La IA llama al lead. No transfiere a un humano.'}].map(mode => (
                      <button key={mode.id} onClick={() => setForm(f => ({ ...f, campaign_mode: mode.id }))} className={`p-3 rounded-lg border text-left transition-all ${form.campaign_mode === mode.id ? 'bg-orange-500/10 border-orange-500/30 ring-1 ring-orange-500/20' : 'bg-black/20 border-white/5 hover:border-white/10'}`} data-testid={`campaign-mode-${mode.id}`}>
                        <p className={`text-[11px] font-medium ${form.campaign_mode === mode.id ? 'text-white' : 'text-white/70'}`}>{mode.label}</p>
                        <p className="text-[9px] text-muted-foreground mt-0.5">{mode.desc}</p>
                      </button>
                    ))}
                  </div>
                  <div data-testid="provider-selector">
                    {!providersLoaded ? (
                      <div className="text-[11px] text-white/50 py-2 flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Detectando proveedores configurados...
                      </div>
                    ) : (() => {
                      const actives = Object.entries(PROVIDERS).filter(([k]) => activeProviders[k]);
                      if (actives.length === 0) {
                        return (
                          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="providers-empty">
                            <p className="text-[11px] text-amber-300">
                              No tienes proveedores de WhatsApp configurados. Ve a{' '}
                              <span className="font-semibold">Ajustes → WhatsApp</span> para configurar uno.
                            </p>
                          </div>
                        );
                      }
                      if (actives.length === 1) {
                        const [k, v] = actives[0];
                        return (
                          <div
                            className={`p-3 rounded-lg border text-left bg-${v.color}-500/10 border-${v.color}-500/30 ring-1 ring-${v.color}-500/20`}
                            data-testid={`provider-${k}`}
                          >
                            <div className="flex items-center gap-2">
                              <MessageCircle className={`w-4 h-4 text-${v.color}-400`} />
                              <p className="text-[11px] font-medium text-white">{v.label}</p>
                              <Badge className="ml-auto text-[9px] bg-white/5 text-white/70">Único proveedor activo</Badge>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">{v.desc}</p>
                          </div>
                        );
                      }
                      return (
                        <div className={`grid grid-cols-1 sm:grid-cols-${actives.length} gap-2`}>
                          {actives.map(([k, v]) => (
                            <button
                              key={k}
                              onClick={() => setForm(f => ({ ...f, provider: k, channel_type: k, template_id: '', template_name: '', template_params: [] }))}
                              className={`p-3 rounded-lg border text-left transition-all ${form.provider === k ? `bg-${v.color}-500/10 border-${v.color}-500/30 ring-1 ring-${v.color}-500/20` : 'bg-black/20 border-white/5 hover:border-white/10'}`}
                              data-testid={`provider-${k}`}
                            >
                              <MessageCircle className={`w-4 h-4 mb-1 ${form.provider === k ? `text-${v.color}-400` : 'text-muted-foreground'}`} />
                              <p className={`text-[11px] font-medium ${form.provider === k ? 'text-white' : 'text-white/70'}`}>{v.label}</p>
                              <p className="text-[9px] text-muted-foreground mt-0.5">{v.desc}</p>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div data-testid="crm-campaign-selector">
                  <Label className="text-xs text-white/60">Campaña CRM (WhatsApp) <span className="text-red-400">*</span></Label>
                  <Select
                    value={form.crm_campaign_id || ''}
                    onValueChange={v => setForm(f => ({ ...f, crm_campaign_id: v }))}
                  >
                    <SelectTrigger
                      className="bg-black/20 border-white/10 text-xs"
                      data-testid="crm-campaign-select-trigger"
                    >
                      <SelectValue placeholder="Selecciona una campaña..." />
                    </SelectTrigger>
                    <SelectContent>
                      {crmCampaigns.length === 0 ? (
                        <div className="px-2 py-2 text-[11px] text-white/60">
                          No hay campañas CRM de WhatsApp activas. Crea una en{' '}
                          <span className="font-semibold">Campañas</span>.
                        </div>
                      ) : crmCampaigns.map(c => (
                        <SelectItem
                          key={c.id}
                          value={c.id}
                          data-testid={`crm-campaign-option-${c.id}`}
                        >
                          {c.name || c.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-white/40 mt-1">
                    Los leads que respondan a esta masiva se asociarán a la campaña CRM seleccionada.
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Tipo de Envio</Label>
                  <Select value={form.schedule_type} onValueChange={v => setForm(f => ({ ...f, schedule_type: v }))}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="schedule-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Enviar Inmediatamente</SelectItem>
                      <SelectItem value="scheduled">Programar Envio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.schedule_type === 'scheduled' && (
                  <DateTimePickerRow
                    value={form.scheduled_at}
                    onChange={v => setForm(f => ({ ...f, scheduled_at: v }))}
                  />
                )}
              </div>
            )}

            {/* Step 3: Audiencia */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Audience mode selector */}
                <div>
                  <Label className="text-xs text-white/60 mb-2 block">Fuente de contactos</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'segments', icon: Users, label: 'Segmentos', desc: 'Leads del CRM' },
                      { id: 'file', icon: FileSpreadsheet, label: 'Archivo', desc: 'CSV o Excel' },
                      { id: 'pipeline', icon: Filter, label: 'Pipeline', desc: 'Por etapa y fecha' },
                    ].map(m => (
                      <button key={m.id} onClick={() => setAudienceMode(m.id)} className={`p-3 rounded-lg border text-center transition-all ${audienceMode === m.id ? 'bg-orange-500/10 border-orange-500/30 ring-1 ring-orange-500/20' : 'bg-black/20 border-white/5 hover:border-white/10'}`} data-testid={`audience-mode-${m.id}`}>
                        <m.icon className={`w-4 h-4 mx-auto mb-1 ${audienceMode === m.id ? 'text-orange-400' : 'text-muted-foreground'}`} />
                        <p className={`text-[11px] font-medium ${audienceMode === m.id ? 'text-white' : 'text-white/70'}`}>{m.label}</p>
                        <p className="text-[9px] text-muted-foreground">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Segments mode */}
                {audienceMode === 'segments' && (
                  <div>
                    <p className="text-[10px] text-muted-foreground mb-3">Los segmentos se generan automaticamente de tus leads. Solo se incluyen leads con telefono.</p>
                    <div className="space-y-2">
                      {segments.map(s => (
                        <button key={s.id} onClick={() => { setForm(f => ({ ...f, segment_id: s.id, segment_name: s.name })); loadRecipients(s.id); }}
                          className={`w-full p-3 rounded-lg border text-left transition-all flex items-center justify-between ${form.segment_id === s.id ? 'bg-orange-500/10 border-orange-500/30 ring-1 ring-orange-500/20' : 'bg-black/20 border-white/5 hover:border-white/10'}`}
                          data-testid={`segment-${s.id}`}
                        >
                          <div>
                            <p className="text-xs font-medium text-white">{s.name}</p>
                            {s.filter?.status && <p className="text-[10px] text-muted-foreground">Filtro: {s.filter.status}</p>}
                          </div>
                          <Badge className="bg-white/5 text-white/70 text-[10px]">{s.count} contactos</Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* File upload mode */}
                {audienceMode === 'file' && (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      <div className="text-[10px] text-blue-300/70 flex-1">
                        <p>
                          Sube un archivo CSV o Excel (.xlsx) con las siguientes columnas
                          {form.template_name ? (
                            <> (para la plantilla <span className="font-mono text-blue-200">{form.template_name}</span>)</>
                          ) : null}:
                        </p>
                        <p className="font-mono mt-0.5" data-testid="template-columns-hint">
                          {dynamicTemplateColumns.join(' | ')}
                        </p>
                        <p className="mt-1 text-blue-300/50">
                          El telefono puede ser de 10 digitos (ej. 5512345678); {APP_NAME} normaliza el formato automaticamente.
                          {extraColumnsSpec.length > 0
                            ? ' Las columnas adicionales corresponden a los parametros de la plantilla seleccionada.'
                            : ' telefono es obligatorio; el resto son opcionales.'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDownloadTemplate}
                      className="w-full h-8 text-[11px] text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/5 border border-emerald-500/20 border-dashed"
                      data-testid="download-template-btn"
                    >
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      {extraColumnsSpec.length > 0
                        ? `Descargar plantilla ${form.template_name || 'personalizada'} (.xlsx)`
                        : 'Descargar plantilla de ejemplo (.xlsx)'}
                    </Button>
                    <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.txt" onChange={handleFileUpload} className="hidden" data-testid="file-input" />
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={fileUploading} className="w-full border-dashed border-white/10 h-20 flex-col gap-1" data-testid="upload-file-btn">
                      {fileUploading ? (
                        <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-orange-400" />
                          <span className="text-xs text-white/60">Arrastra o haz clic para subir CSV / Excel</span>
                        </>
                      )}
                    </Button>
                    {uploadedFile && (
                      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                          <div>
                            <p className="text-xs text-emerald-400">{uploadedFile.name}</p>
                            <p className="text-[10px] text-muted-foreground">{uploadedFile.total} contactos cargados</p>
                          </div>
                        </div>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      </div>
                    )}
                  </div>
                )}

                {/* Pipeline mode */}
                {audienceMode === 'pipeline' && (
                  <div className="space-y-3">
                    <p className="text-[10px] text-muted-foreground">Selecciona etapas del pipeline y un rango de fechas para encontrar leads con oportunidades.</p>
                    <div>
                      <Label className="text-xs text-white/60 mb-1.5 block">Etapas del Pipeline</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {pipelineStages.map(s => {
                          const sel = selectedStages.includes(s.id);
                          return (
                            <button key={s.id} onClick={() => setSelectedStages(prev => sel ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${sel ? 'bg-orange-500/10 text-orange-400 border-orange-500/30' : 'bg-black/20 text-white/40 border-white/5 hover:border-white/10'}`}
                              data-testid={`stage-${s.id}`}>
                              {s.name}
                            </button>
                          );
                        })}
                        {pipelineStages.length === 0 && <p className="text-[10px] text-muted-foreground">No hay etapas configuradas</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <DatePickerField
                        label="Desde"
                        value={pipelineDateFrom}
                        onChange={v => {
                          setPipelineDateFrom(v);
                          // Si Desde > Hasta actual, limpiar Hasta.
                          if (v && pipelineDateTo && v > pipelineDateTo) setPipelineDateTo('');
                        }}
                        testid="pipeline-date-from"
                      />
                      <DatePickerField
                        label="Hasta"
                        value={pipelineDateTo}
                        onChange={v => setPipelineDateTo(v)}
                        testid="pipeline-date-to"
                        min={pipelineDateFrom || undefined}
                      />
                    </div>
                    <Button onClick={loadPipelineLeads} disabled={pipelineLoading || !selectedStages.length} className="w-full bg-orange-600 hover:bg-orange-500 text-xs" data-testid="search-pipeline-btn">
                      {pipelineLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Filter className="w-3.5 h-3.5 mr-1" />}
                      Buscar Leads del Pipeline
                    </Button>
                  </div>
                )}

                {recipients.length > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                    <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {recipients.length} destinatarios seleccionados</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Primeros 3: {recipients.slice(0, 3).map(r => r.name || r.phone).join(', ')}{recipients.length > 3 ? '...' : ''}</p>
                  </div>
                )}

                {optOutWarning && optOutWarning.count > 0 && (
                  <div
                    className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400"
                    data-testid="opt-out-warning"
                  >
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <span>⚠️</span>
                      <span>
                        {optOutWarning.count} de {optOutWarning.total} contactos ya solicitaron no ser contactados y serán omitidos al enviar la campaña.
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleExportOptOuts}
                      className="mt-2 text-xs text-amber-400 underline hover:text-amber-300 transition-colors"
                      data-testid="export-opt-outs-inline-btn"
                    >
                      ⬇️ Exportar lista completa de bajas
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Contenido */}
            {step === 1 && (
              <div className="space-y-4">
                {form.campaign_mode === 'whatsapp' ? (
                  <TemplateSelectorBlock
                    provider={form.provider}
                    templates={templates}
                    templatesLoading={templatesLoading}
                    templatesSyncing={templatesSyncing}
                    onSync={handleSyncTemplates}
                    form={form}
                    setForm={setForm}
                    placeholderIndexes={placeholderIndexes}
                    recipientFields={extractRecipientFields(form.recipients)}
                    firstRecipient={firstRecipient}
                    previewText={templatePreviewText}
                    selectedTemplate={selectedTemplate}
                    onOpenTest={() => setTestSendOpen(true)}
                  />
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs text-white/60">Saludo inicial del agente IA *</Label>
                      <Input value={form.ai_greeting || ''} onChange={e => setForm(f => ({ ...f, ai_greeting: e.target.value }))} placeholder="Hola, soy Sofia del equipo..." className="bg-black/20 border-white/10 text-sm" data-testid="ai-call-greeting" />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Prompt de llamada IA *</Label>
                      <Textarea value={form.call_prompt} onChange={e => setForm(f => ({ ...f, call_prompt: e.target.value }))} placeholder="Llama al prospecto, preséntate, valida interés, recaba necesidades y resume hallazgos relevantes..." className="bg-black/20 border-white/10 text-sm min-h-[120px]" data-testid="call-prompt" />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Prompt completo del agente IA</Label>
                      <Textarea value={form.ai_full_prompt || ''} onChange={e => setForm(f => ({ ...f, ai_full_prompt: e.target.value }))} placeholder="Rol completo, personalidad, límites y tono..." className="bg-black/20 border-white/10 text-sm min-h-[100px]" data-testid="ai-call-full-prompt" />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Instrucciones y objetivos de la llamada</Label>
                      <Textarea value={form.ai_call_objectives || ''} onChange={e => setForm(f => ({ ...f, ai_call_objectives: e.target.value }))} placeholder="Objetivo primario, calificación, cierre de cita, etc." className="bg-black/20 border-white/10 text-sm min-h-[90px]" data-testid="ai-call-objectives" />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Script de conversación sugerido</Label>
                      <Textarea value={form.ai_conversation_script || ''} onChange={e => setForm(f => ({ ...f, ai_conversation_script: e.target.value }))} placeholder="Apertura, preguntas de descubrimiento, manejo de objeciones, cierre." className="bg-black/20 border-white/10 text-sm min-h-[90px]" data-testid="ai-call-script" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-white/10 bg-black/20" data-testid="ai-call-db-permissions">
                      <p className="col-span-2 text-xs text-white/70">Permisos de consulta de base de datos</p>
                      {[
                        ['inventory', 'Inventario'], ['prices', 'Precios'], ['appointments', 'Citas'], ['customer_history', 'Historial cliente'],
                      ].map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-xs text-white/80">
                          <input type="checkbox" checked={!!form.ai_db_permissions?.[key]} onChange={e => setForm(f => ({ ...f, ai_db_permissions: { ...(f.ai_db_permissions || {}), [key]: e.target.checked } }))} data-testid={`ai-db-perm-${key}`} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-3 rounded-lg border border-white/10 bg-black/20" data-testid="ai-call-metrics-config">
                      <p className="col-span-2 text-xs text-white/70">Métricas y seguimiento</p>
                      {[
                        ['track_duration', 'Duración'], ['track_outcome', 'Resultado'], ['track_intent', 'Intención'], ['track_followups', 'Seguimientos'],
                      ].map(([key, label]) => (
                        <label key={key} className="flex items-center gap-2 text-xs text-white/80">
                          <input type="checkbox" checked={!!form.ai_metrics_config?.[key]} onChange={e => setForm(f => ({ ...f, ai_metrics_config: { ...(f.ai_metrics_config || {}), [key]: e.target.checked } }))} data-testid={`ai-metric-${key}`} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Perfil de voz</Label>
                      <Select value={form.voice_profile} onValueChange={v => setForm(f => ({ ...f, voice_profile: v }))}>
                        <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="voice-profile"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="female_es">Voz femenina ES</SelectItem>
                          <SelectItem value="male_es">Voz masculina ES</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Message Spinning / Variations */}
                <div className="border-t border-white/5 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-xs font-medium text-white">Variaciones de Mensaje (Spinning)</p>
                      <p className="text-[10px] text-muted-foreground">Agrega versiones alternativas del mensaje para evitar contenido repetido y reducir riesgo de ban</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, message_variations: [...f.message_variations, ''] }))} className="text-[10px] h-7 border-white/10" data-testid="add-variation-btn">
                      <Plus className="w-3 h-3 mr-0.5" /> Variacion
                    </Button>
                  </div>
                  {form.message_variations.length > 0 && (
                    <div className="space-y-2">
                      {form.message_variations.map((v, i) => (
                        <div key={i} className="flex gap-2 items-start">
                          <div className="flex-1">
                            <Label className="text-[10px] text-white/40">Variacion {i + 1}</Label>
                            <Textarea value={v} onChange={e => {
                              const vars = [...form.message_variations];
                              vars[i] = e.target.value;
                              setForm(f => ({ ...f, message_variations: vars }));
                            }} placeholder={`Alternativa ${i + 1} del mensaje...`} className="bg-black/20 border-white/10 text-xs min-h-[70px]" data-testid={`variation-${i}`} />
                          </div>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 mt-5" onClick={() => {
                            const vars = form.message_variations.filter((_, j) => j !== i);
                            setForm(f => ({ ...f, message_variations: vars }));
                          }} data-testid={`remove-variation-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      <div className="p-2 rounded bg-amber-500/5 border border-amber-500/10">
                        <p className="text-[9px] text-amber-400/70">El sistema alternara entre el mensaje principal y las {form.message_variations.length} variaciones de forma aleatoria para cada destinatario.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Media URL: solo providers que NO embedden la imagen en el template HSM.
                    Gupshup toma la media directamente de containerMeta.mediaUrl del template. */}
                {form.provider !== 'gupshup' && (
                  <div>
                    <Label className="text-xs text-white/60">URL de Imagen/Video (opcional)</Label>
                    <Input value={form.media_url || ''} onChange={e => setForm(f => ({ ...f, media_url: e.target.value }))} placeholder="https://..." className="bg-black/20 border-white/10 text-xs" data-testid="media-url" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Agrega una imagen o video al mensaje</p>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Configuracion */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                  <p className="text-[10px] text-blue-400 flex items-center gap-1"><Info className="w-3 h-3" /> Para canales oficiales (WABA) se permite alto volumen. Twilio y Gupshup tienen sus propios limites.</p>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Mensajes por minuto</Label>
                  <Input type="number" value={form.rate_config.msgs_per_minute} onChange={e => setForm(f => ({ ...f, rate_config: { ...f.rate_config, msgs_per_minute: +e.target.value } }))} className="bg-black/20 border-white/10 text-xs" data-testid="rate-per-min" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">Mensajes por hora</Label>
                  <Input type="number" value={form.rate_config.msgs_per_hour} onChange={e => setForm(f => ({ ...f, rate_config: { ...f.rate_config, msgs_per_hour: +e.target.value } }))} className="bg-black/20 border-white/10 text-xs" data-testid="rate-per-hour" />
                </div>
              </div>
            )}

            {/* Step 5: Anti-Baneo */}
            {step === 4 && (
              <div className="space-y-5">
                <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                  <div>
                    <p className="text-xs font-medium text-white">Habilitar Proteccion Anti-Baneo</p>
                    <p className="text-[10px] text-muted-foreground">Aplica limites de tasa inteligentes y rotacion de cuentas</p>
                  </div>
                  <Switch checked={form.anti_ban.enabled} onCheckedChange={v => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, enabled: v } }))} data-testid="antiban-toggle" />
                </div>

                {form.anti_ban.enabled && (
                  <>
                    <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10 flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                      <p className="text-[10px] text-blue-300/70">La API oficial (WABA) tiene compliance integrado. Las funciones anti-baneo son opcionales pero optimizan tasas de entrega y experiencia de usuario.</p>
                    </div>

                    {/* Mode Selector */}
                    <div>
                      <Label className="text-xs text-white/60 mb-2 block">Modo de Proteccion</Label>
                      <p className="text-[10px] text-muted-foreground mb-2">Elige que tan agresiva debe ser la proteccion anti-baneo</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'conservative', name: 'Conservador', desc: 'Mas lento, mas seguro', color: 'emerald' },
                          { id: 'moderate', name: 'Moderado', desc: 'Enfoque equilibrado', color: 'orange' },
                          { id: 'aggressive', name: 'Agresivo', desc: 'Mas rapido, mayor riesgo', color: 'red' },
                        ].map(m => (
                          <button key={m.id} onClick={() => setAntiBanMode(m.id)} className={`p-3 rounded-lg border text-center transition-all ${form.anti_ban.mode === m.id ? `bg-${m.color}-500/10 border-${m.color}-500/30 ring-1 ring-${m.color}-500/20` : 'bg-black/20 border-white/5'}`} data-testid={`antiban-mode-${m.id}`}>
                            <p className={`text-xs font-medium ${form.anti_ban.mode === m.id ? 'text-white' : 'text-white/70'}`}>{m.name}</p>
                            <p className="text-[9px] text-muted-foreground mt-0.5">{m.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Weekend & Business Hours */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                        <div>
                          <p className="text-[11px] text-white">Respetar Fines de Semana</p>
                          <p className="text-[9px] text-muted-foreground">Pausar los fines de semana</p>
                        </div>
                        <Switch checked={form.anti_ban.respect_weekends} onCheckedChange={v => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, respect_weekends: v } }))} data-testid="antiban-weekends" />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                        <div>
                          <p className="text-[11px] text-white">Solo Horario Laboral</p>
                          <p className="text-[9px] text-muted-foreground">9 AM - 6 PM</p>
                        </div>
                        <Switch checked={form.anti_ban.business_hours} onCheckedChange={v => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, business_hours: v } }))} data-testid="antiban-hours" />
                      </div>
                    </div>

                    {/* Delays */}
                    <div>
                      <p className="text-xs text-white/60 mb-1">Aleatorizar Retrasos</p>
                      <p className="text-[10px] text-muted-foreground mb-2">Anade variaciones similares a las humanas al tiempo de los mensajes</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] text-white/40">Retraso Minimo (segundos)</Label>
                          <Input type="number" value={form.anti_ban.delay_min} onChange={e => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, delay_min: +e.target.value } }))} className="bg-black/20 border-white/10 text-xs" data-testid="antiban-delay-min" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-white/40">Retraso Maximo (segundos)</Label>
                          <Input type="number" value={form.anti_ban.delay_max} onChange={e => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, delay_max: +e.target.value } }))} className="bg-black/20 border-white/10 text-xs" data-testid="antiban-delay-max" />
                        </div>
                      </div>
                    </div>

                    {/* Rotation & Variation */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                        <div>
                          <p className="text-[11px] text-white">Rotacion Inteligente de Cuentas</p>
                          <p className="text-[9px] text-muted-foreground">Distribuye mensajes entre las cuentas seleccionadas</p>
                        </div>
                        <Switch checked={form.anti_ban.account_rotation} onCheckedChange={v => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, account_rotation: v } }))} data-testid="antiban-rotation" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-white/40">Periodo de Enfriamiento (minutos)</Label>
                        <Input type="number" value={form.anti_ban.cooldown_minutes} onChange={e => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, cooldown_minutes: +e.target.value } }))} className="bg-black/20 border-white/10 text-xs" data-testid="antiban-cooldown" />
                        <p className="text-[9px] text-muted-foreground mt-0.5">Tiempo de descanso entre envios de alto volumen</p>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-black/20 border border-white/5">
                        <div>
                          <p className="text-[11px] text-white">Variacion de Mensaje</p>
                          <p className="text-[9px] text-muted-foreground">Anade ligeras variaciones para evitar contenido identico</p>
                        </div>
                        <Switch checked={form.anti_ban.message_variation} onCheckedChange={v => setForm(f => ({ ...f, anti_ban: { ...f.anti_ban, message_variation: v } }))} data-testid="antiban-variation" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 6: Review */}
            {step === 5 && (
              <div className="space-y-4" data-testid="review-step">
                <ReviewRow label="Campana" value={form.name || '-'} />
                <ReviewRow label="Proveedor" value={PROVIDERS[form.provider]?.label || form.provider || '-'} />
                {form.campaign_mode === 'whatsapp' && <ReviewRow label="Plantilla" value={form.template_name || '(no seleccionada)'} />}
                <ReviewRow label="Envio" value={form.schedule_type === 'immediate' ? 'Inmediato' : `Programado: ${form.scheduled_at}`} />
                <ReviewRow label="Audiencia" value={`${form.segment_name || 'Sin segmento'} (${form.recipients?.length || 0} destinatarios)`} />
                <ReviewRow label="Modo" value={form.campaign_mode === 'ai_calls' ? 'Llamadas IA' : 'WhatsApp'} />
                <ReviewRow
                  label={form.campaign_mode === 'ai_calls' ? 'Prompt de llamada' : 'Mensaje (preview)'}
                  value={(() => {
                    const src = form.campaign_mode === 'ai_calls' ? form.call_prompt : templatePreviewText;
                    if (!src) return '-';
                    return src.length > 100 ? src.slice(0, 100) + '...' : src;
                  })()}
                />
                {form.campaign_mode === 'ai_calls' && <ReviewRow label="Saludo IA" value={(form.ai_greeting || '-')} />}
                {form.campaign_mode === 'ai_calls' && <ReviewRow label="Objetivos IA" value={(form.ai_call_objectives || '-').slice(0, 100)} />}
                {form.campaign_mode === 'whatsapp' && form.message_variations?.length > 0 && <ReviewRow label="Variaciones" value={`${form.message_variations.length} alternativas configuradas`} />}
                {form.media_url && <ReviewRow label="Media" value={form.media_url} />}
                {form.campaign_mode === 'ai_calls' && <ReviewRow label="Voz" value={form.voice_profile} />}
                <ReviewRow label="Anti-Baneo" value={form.anti_ban.enabled ? `Activo - ${form.anti_ban.mode}` : 'Desactivado'} />
                <ReviewRow label="Retraso" value={form.anti_ban.enabled ? `${form.anti_ban.delay_min}-${form.anti_ban.delay_max}s entre mensajes` : 'Sin retraso'} />

                {sendDisabledReason && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-2" data-testid="review-missing">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                    <p className="text-xs text-amber-300 font-medium">{sendDisabledReason}. Revisa los pasos anteriores.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Navigation */}
          <div className="p-4 border-t border-white/5 flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => step > 0 ? setStep(step - 1) : setWizard(false)} className="text-xs border-white/10" data-testid="wizard-prev">
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {step === 0 ? 'Cancelar' : 'Anterior'}
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={saveDraft} disabled={saving} className="text-xs border-white/10" data-testid="wizard-save-draft">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />} Guardar Borrador
              </Button>
              {step < STEPS.length - 1 ? (() => {
                // Validación por paso: step 0 requiere proveedor activo + datetime si scheduled.
                const noProviders = providersLoaded && !Object.values(activeProviders).some(Boolean);
                const needsSchedule = form.schedule_type === 'scheduled';
                const scheduleIncomplete = needsSchedule && (!form.scheduled_at || !form.scheduled_at.includes('T') || !form.scheduled_at.split('T')[1]);
                const nextDisabled = (step === 0 && (noProviders || scheduleIncomplete));
                const nextTitle = noProviders
                  ? 'Configura al menos un proveedor de WhatsApp'
                  : scheduleIncomplete ? 'Selecciona fecha y hora de envío' : '';
                return (
                  <Button
                    size="sm"
                    onClick={() => setStep(step + 1)}
                    disabled={nextDisabled}
                    title={nextTitle}
                    className="bg-orange-600 hover:bg-orange-500 text-xs"
                    data-testid="wizard-next"
                  >
                    Siguiente <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                );
              })() : (
                <Button
                  size="sm"
                  onClick={sendCampaign}
                  disabled={sending || Boolean(sendDisabledReason)}
                  title={sendDisabledReason || ''}
                  className="bg-emerald-600 hover:bg-emerald-500 text-xs"
                  data-testid="wizard-send"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Enviar Campana
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail panel */}
      <CampaignDetailDialog
        campaign={detailCampaign}
        intents={intents}
        loading={intentsLoading}
        onClose={() => { setDetailCampaign(null); setIntents([]); }}
      />

      {/* Test send dialog (paso 3 wizard) */}
      <TestSendDialog
        open={testSendOpen}
        onClose={() => setTestSendOpen(false)}
        provider={form.provider}
        template={selectedTemplate}
        templateParams={form.template_params}
        placeholderIndexes={placeholderIndexes}
      />

      {/* Debug Info modal (admin/superadmin only) */}
      {debugCampaign && (
        <Dialog open={!!debugCampaign} onOpenChange={() => setDebugCampaign(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info size={16} />
                Debug Info — {debugCampaign.name}
              </DialogTitle>
            </DialogHeader>
            <DebugInfoPanel campaign={debugCampaign} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function CampaignRow({ campaign: c, onDelete, onPause, onOpen, onReload, onDebug }) {
  const statusColors = {
    draft: 'bg-white/5 text-white/50',
    scheduled: 'bg-blue-500/20 text-blue-400',
    sending: 'bg-amber-500/20 text-amber-400',
    completed: 'bg-emerald-500/20 text-emerald-400',
    failed: 'bg-red-500/20 text-red-400',
    paused: 'bg-orange-500/20 text-orange-400',
  };
  const statusLabels = {
    draft: 'Borrador', scheduled: 'Programada', sending: 'Enviando',
    completed: 'Completada', failed: 'Fallida', paused: 'Pausada',
  };

  // Countdown live solo cuando esta programada y hay fecha.
  const isScheduled = c.status === 'scheduled' && !!c.scheduled_at_utc;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isScheduled) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [isScheduled]);

  // Si la fecha ya paso, re-pedimos campanas cada 10s hasta que el dispatcher
  // transicione el status (scheduled -> sending/completed/failed).
  const diffMs = isScheduled ? new Date(c.scheduled_at_utc).getTime() - now : 0;
  const dueReached = isScheduled && diffMs <= 0;
  useEffect(() => {
    if (!dueReached || !onReload) return undefined;
    const poll = setInterval(() => onReload && onReload(), 10000);
    return () => clearInterval(poll);
  }, [dueReached, onReload]);

  const formatScheduled = () => {
    try {
      return new Date(c.scheduled_at_utc).toLocaleString('es-MX', {
        weekday: 'long', day: 'numeric', month: 'short',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch (_e) {
      return String(c.scheduled_at_utc || '');
    }
  };
  const formatCountdown = () => {
    const total = Math.max(0, Math.floor(diffMs / 1000));
    const hh = String(Math.floor(total / 3600)).padStart(2, '0');
    const mm = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
    const ss = String(total % 60).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const stopClick = (fn) => (e) => { e.stopPropagation(); fn && fn(); };
  return (
    <Card
      onClick={onOpen}
      className="bg-card/50 backdrop-blur-md border-white/5 hover:border-white/10 cursor-pointer transition-colors"
      data-testid={`campaign-${c.id}`}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center shrink-0">
          <Megaphone className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-white truncate">{c.name || 'Sin nombre'}</h3>
            <Badge className={`text-[9px] ${statusColors[c.status] || ''}`}>{statusLabels[c.status] || c.status}</Badge>
            <Badge className="text-[9px] bg-white/5 text-white/50">{c.provider || c.channel_type}</Badge>
            <Badge className="text-[9px] bg-orange-500/10 text-orange-300">{c.campaign_mode === 'ai_calls' ? 'Llamadas IA' : 'WhatsApp'}</Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-[10px] text-muted-foreground">
            <span><Users className="w-3 h-3 inline mr-0.5" /> {c.total_recipients || 0} dest.</span>
            {c.stats?.sent > 0 && <span className="text-emerald-400"><CheckCircle2 className="w-3 h-3 inline mr-0.5" /> {c.stats.sent} env.</span>}
            {c.stats?.failed > 0 && <span className="text-red-400">{c.stats.failed} fall.</span>}
            <span><Clock className="w-3 h-3 inline mr-0.5" /> {c.created_at ? new Date(c.created_at).toLocaleDateString('es-MX') : ''}</span>
          </div>
          {isScheduled && (
            <div className="mt-1 space-y-0.5" data-testid={`scheduled-info-${c.id}`}>
              <div className="text-[10px] text-blue-300 flex items-center gap-1" data-testid={`scheduled-at-${c.id}`}>
                <CalendarIcon className="w-3 h-3" />
                Programada para: <span className="font-medium">{formatScheduled()}</span>
              </div>
              <div className="text-[10px] text-amber-300/90 flex items-center gap-1" data-testid={`scheduled-countdown-${c.id}`}>
                <Clock className="w-3 h-3" />
                {dueReached
                  ? <span>Enviando en breve...</span>
                  : <span>Envia en: <span className="font-mono font-medium">{formatCountdown()}</span></span>
                }
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onDebug && (
            <button
              onClick={stopClick(onDebug)}
              className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors"
              title="Debug Info"
              data-testid={`debug-campaign-${c.id}`}
            >
              <Info size={14} />
            </button>
          )}
          {c.status === 'sending' && (
            <Button variant="ghost" size="sm" onClick={stopClick(onPause)} className="h-7 w-7 p-0 text-amber-400" data-testid={`pause-${c.id}`}><Pause className="w-3.5 h-3.5" /></Button>
          )}
          <Button variant="ghost" size="sm" onClick={stopClick(onDelete)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-campaign-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-lg bg-black/20 border border-white/5">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-24 shrink-0">{label}</span>
      <span className="text-xs text-white">{value}</span>
    </div>
  );
}


function TemplateSelectorBlock({
  provider, templates, templatesLoading, templatesSyncing, onSync,
  form, setForm, placeholderIndexes, recipientFields, firstRecipient, previewText, selectedTemplate,
  onOpenTest,
}) {
  // Filtro por module_type: solo plantillas asignadas explícitamente a "campanas".
  // Las asignadas a otro módulo o sin módulo quedan ocultas (deben configurarse
  // desde Ajustes → WhatsApp → Plantillas).
  const filteredTemplates = templates.filter(
    (t) => t.module_type === 'campanas'
  );
  const hiddenByModule = templates.length - filteredTemplates.length;
  // Filtro estricto: solo "campanas". No hay agrupación secundaria por orden.
  const sortedTemplates = filteredTemplates;
  const hasTemplates = sortedTemplates.length > 0;

  const selectTemplate = (tplId) => {
    const tpl = sortedTemplates.find(t => t.id === tplId);
    if (!tpl) return;
    const indexes = extractPlaceholderIndexes(tpl.body || '');
    const params = indexes.map(idx => ({ index: idx, type: 'fixed', value: '', field_name: null }));
    setForm(f => ({ ...f, template_id: tpl.id, template_name: tpl.name || tpl.element_name || '', template_params: params }));
  };

  const updateParam = (idx, patch) => {
    setForm(f => {
      const existing = (f.template_params || []).find(p => p.index === idx);
      let next = [...(f.template_params || [])];
      if (existing) {
        next = next.map(p => p.index === idx ? { ...p, ...patch } : p);
      } else {
        next.push({ index: idx, type: 'fixed', value: '', field_name: null, ...patch });
      }
      return { ...f, template_params: next };
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs text-white/60">Plantilla aprobada *</Label>
        </div>
        <p className="text-[10px] text-muted-foreground mb-1.5">
          Solo se permiten plantillas APPROVED del proveedor seleccionado.
        </p>
        {templatesLoading ? (
          <div className="text-[11px] text-white/50 py-3 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Cargando plantillas...
          </div>
        ) : hasTemplates ? (
          <>
          <Select value={form.template_id || ''} onValueChange={selectTemplate}>
            <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="template-selector">
              <SelectValue placeholder="Selecciona una plantilla" />
            </SelectTrigger>
            <SelectContent>
              {sortedTemplates.map(t => (
                <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                  <div className="flex flex-col">
                    <span className="text-xs">
                      {(t.name || t.element_name)}
                    </span>
                    <span className="text-[9px] text-muted-foreground truncate max-w-[400px]">
                      {(t.body || '').slice(0, 120)}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hiddenByModule > 0 && (
            <p className="text-xs text-gray-500 mt-1" data-testid="template-module-filter-info">
              Mostrando plantillas asignadas a este módulo. Para ver todas, ve a Configuración → WhatsApp → Plantillas.
            </p>
          )}
          </>
        ) : (
          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20" data-testid="templates-empty">
            <p className="text-[11px] text-amber-300">
              No hay plantillas aprobadas para este proveedor asignadas al módulo Campañas. Configura tus plantillas en{' '}
              <span className="font-semibold">Ajustes → WhatsApp → Plantillas</span> y asígnalas al módulo "Campañas".
            </p>
          </div>
        )}
      </div>

      {selectedTemplate && placeholderIndexes.length > 0 && (
        <div className="space-y-2" data-testid="template-params-editor">
          <Label className="text-xs text-white/60">Parámetros del template</Label>
          <p className="text-[10px] text-muted-foreground mb-1">
            Configura el valor de cada <code className="bg-black/30 px-1 rounded">{'{{N}}'}</code> detectado.
          </p>
          {placeholderIndexes.map(idx => {
            const param = (form.template_params || []).find(p => p.index === idx) || { index: idx, type: 'fixed', value: '', field_name: null };
            return (
              <div
                key={idx}
                className="p-2.5 rounded-lg bg-black/20 border border-white/5 space-y-2"
                data-testid={`template-param-row-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] text-white/70">Parámetro <code className="bg-black/30 px-1 rounded">{`{{${idx}}}`}</code></Label>
                  <div className="flex gap-1 bg-black/30 rounded p-0.5">
                    <button
                      onClick={() => updateParam(idx, { type: 'fixed', field_name: null })}
                      className={`px-2 py-0.5 rounded text-[10px] transition-all ${param.type === 'fixed' ? 'bg-orange-500/20 text-orange-300' : 'text-white/50'}`}
                      data-testid={`param-${idx}-type-fixed`}
                    >
                      Texto fijo
                    </button>
                    <button
                      onClick={() => updateParam(idx, { type: 'field', value: '' })}
                      className={`px-2 py-0.5 rounded text-[10px] transition-all ${param.type === 'field' ? 'bg-orange-500/20 text-orange-300' : 'text-white/50'}`}
                      data-testid={`param-${idx}-type-field`}
                    >
                      Campo del recipient
                    </button>
                    <button
                      onClick={() => updateParam(idx, { type: 'col_excel', value: '', field_name: null })}
                      className={`px-2 py-0.5 rounded text-[10px] transition-all ${param.type === 'col_excel' ? 'bg-orange-500/20 text-orange-300' : 'text-white/50'}`}
                      data-testid={`param-${idx}-type-col_excel`}
                    >
                      Columna Excel
                    </button>
                  </div>
                </div>
                {param.type === 'fixed' && (
                  <Input
                    value={param.value || ''}
                    onChange={e => updateParam(idx, { value: e.target.value })}
                    placeholder={`Valor fijo para {{${idx}}}`}
                    className="bg-black/20 border-white/10 text-xs"
                    data-testid={`param-${idx}-value-input`}
                  />
                )}
                {param.type === 'field' && (
                  <Select
                    value={param.field_name || ''}
                    onValueChange={v => updateParam(idx, { field_name: v })}
                  >
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid={`param-${idx}-field-selector`}>
                      <SelectValue placeholder="Selecciona campo del recipient" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipientFields.map(fn => (
                        <SelectItem key={fn} value={fn} data-testid={`param-${idx}-field-${fn}`}>
                          {fn}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {param.type === 'col_excel' && (
                  <div
                    className="px-2.5 py-1.5 rounded bg-blue-500/5 border border-blue-500/10 text-[10px] text-blue-300/80 flex items-center gap-1.5"
                    data-testid={`param-${idx}-col-excel-hint`}
                  >
                    <FileSpreadsheet className="w-3 h-3" />
                    Se rellenará desde la columna <code className="bg-black/30 px-1 rounded text-blue-200">{`{{${idx}}}`}</code> del Excel/CSV cargado.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedTemplate && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onOpenTest}
            disabled={provider === 'meta_cloud'}
            title={provider === 'meta_cloud' ? 'Envío de prueba no disponible para Meta Cloud aún' : ''}
            className="h-7 px-2.5 text-[10px] border-white/10 hover:border-white/20 gap-1"
            data-testid="open-test-send-btn"
          >
            <Send className="w-3 h-3" />
            Enviar mensaje de prueba
          </Button>
        </div>
      )}

      {selectedTemplate && (
        <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/10" data-testid="template-live-preview">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] text-green-400">Vista previa en vivo</p>
            <p className="text-[9px] text-muted-foreground">
              {firstRecipient ? `Recipient: ${firstRecipient.name || firstRecipient.phone}` : 'Sin recipients cargados'}
            </p>
          </div>
          {selectedTemplate.media_url && (() => {
            const tplType = String(selectedTemplate.template_type || 'IMAGE').toUpperCase();
            const isVideo = tplType === 'VIDEO';
            return (
              <div className="mb-2 flex justify-center relative" data-testid="template-media-preview">
                {isVideo ? (
                  <>
                    <video
                      src={selectedTemplate.media_url}
                      controls
                      className="w-full max-h-32 object-contain rounded-lg border border-white/5 bg-black/40"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      data-testid="template-media-video"
                    />
                    <span
                      className="absolute top-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-[9px] text-white/90 pointer-events-none"
                      data-testid="template-media-video-badge"
                    >
                      <Video className="w-2.5 h-2.5" /> VIDEO
                    </span>
                  </>
                ) : (
                  <img
                    src={selectedTemplate.media_url}
                    alt="Template media"
                    className="w-full max-h-32 object-contain rounded-lg border border-white/5 bg-black/20"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid="template-media-image"
                  />
                )}
              </div>
            );
          })()}
          {selectedTemplate.media_url && (
            <p className="text-[9px] text-green-400/50 mb-2 truncate text-center">
              {(selectedTemplate.template_type || 'IMAGE').toLowerCase()}
            </p>
          )}
          <p className="text-xs text-white/80 whitespace-pre-wrap" data-testid="preview-body">
            {previewText || selectedTemplate.body || '—'}
          </p>
        </div>
      )}
    </div>
  );
}

const INTENT_STATUS_STYLE = {
  sent:             { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Enviado' },
  failed:           { bg: 'bg-red-500/10',     text: 'text-red-400',     label: 'Fallido' },
  sending:          { bg: 'bg-amber-500/10',   text: 'text-amber-400',   label: 'Enviando' },
  retry:            { bg: 'bg-orange-500/10',  text: 'text-orange-400',  label: 'Reintentar' },
  pending:          { bg: 'bg-white/5',        text: 'text-white/50',    label: 'Pendiente' },
  skipped:          { bg: 'bg-slate-500/10',   text: 'text-slate-400',   label: 'Omitido' },
  delivered:        { bg: 'bg-blue-500/10',    text: 'text-blue-400',    label: 'Entregado' },
  read:             { bg: 'bg-violet-500/10',  text: 'text-violet-400',  label: 'Leído' },
  blocked_by_meta:  { bg: 'bg-orange-500/10',  text: 'text-orange-400',  label: 'Bloqueado por Meta' },
};

function fmtSentAt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    // UTC-6 fijo (México eliminó DST en 2022, CDMX es UTC-6 permanente)
    const offsetMs = -6 * 60 * 60 * 1000;
    const local = new Date(d.getTime() + offsetMs);
    const dd = String(local.getUTCDate()).padStart(2, '0');
    const mm = String(local.getUTCMonth() + 1).padStart(2, '0');
    const hh = String(local.getUTCHours()).padStart(2, '0');
    const mi = String(local.getUTCMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}:${mi}`;
  } catch { return '—'; }
}

function CampaignDetailDialog({ campaign, intents, loading, onClose }) {
  if (!campaign) return null;
  const open = Boolean(campaign);
  const stats = campaign.stats || {};
  const counts = { sent: 0, failed: 0, sending: 0, pending: 0, retry: 0, skipped: 0, delivered: 0, read: 0, blocked_by_meta: 0 };
  for (const i of intents || []) {
    const s = i.status || 'pending';
    if (counts[s] !== undefined) counts[s] += 1;
  }
  const provider = campaign.provider || campaign.channel_type || '—';
  const statusBadgeCls = {
    completed: 'bg-emerald-500/20 text-emerald-400',
    sending: 'bg-amber-500/20 text-amber-400',
    failed: 'bg-red-500/20 text-red-400',
    scheduled: 'bg-blue-500/20 text-blue-400',
    paused: 'bg-orange-500/20 text-orange-400',
    draft: 'bg-white/5 text-white/60',
  }[campaign.status] || 'bg-white/5 text-white/60';

  const handleExportBlocked = async () => {
    try {
      const response = await api.get(`/mass-campaigns/${campaign.id}/export`, {
        params: { status: 'blocked_by_meta' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bloqueados_por_meta_${campaign.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al exportar bloqueados por Meta');
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl p-0 gap-0 bg-card border-white/10" data-testid="campaign-detail-dialog">
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-medium text-white" data-testid="detail-name">{campaign.name || 'Sin nombre'}</h2>
            <Badge className={`text-[10px] ${statusBadgeCls}`} data-testid="detail-status">{campaign.status}</Badge>
            <Badge className="text-[10px] bg-white/5 text-white/60" data-testid="detail-provider">{provider}</Badge>
            {campaign.template_name && <Badge className="text-[10px] bg-orange-500/10 text-orange-300" data-testid="detail-template">{campaign.template_name}</Badge>}
            <span className="text-[10px] text-muted-foreground ml-auto">
              {campaign.created_at ? new Date(campaign.created_at).toLocaleString('es-MX') : ''}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px]">
            <span className="text-emerald-400" data-testid="stat-sent">
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
              {counts.sent || stats.sent || 0} enviados
            </span>
            <span className="text-red-400" data-testid="stat-failed">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {counts.failed || stats.failed || 0} fallidos
            </span>
            <span className="text-amber-400" data-testid="stat-sending">
              {counts.sending || 0} enviando
            </span>
            <span className="text-orange-400" data-testid="stat-retry">
              {counts.retry || 0} en reintento
            </span>
            <span className="text-white/60" data-testid="stat-pending">
              {counts.pending || 0} pendientes
            </span>
            <span className="text-slate-400" data-testid="stat-skipped">
              {counts.skipped || 0} omitidos
            </span>
            {counts.delivered > 0 && (
              <span className="text-blue-400" data-testid="stat-delivered">
                <span className="font-medium">{counts.delivered}</span> entregados
              </span>
            )}
            {counts.read > 0 && (
              <span className="text-violet-400" data-testid="stat-read">
                <span className="font-medium">{counts.read}</span> leídos
              </span>
            )}
            {counts.blocked_by_meta > 0 && (
              <>
                <span className="text-orange-400" data-testid="stat-blocked-by-meta">
                  <span className="font-medium">{counts.blocked_by_meta}</span> bloqueados por Meta
                </span>
                <button
                  type="button"
                  onClick={handleExportBlocked}
                  className="flex items-center gap-1 px-2 py-1 rounded-md border border-orange-500/30 text-orange-400 hover:bg-orange-500/10 transition-colors text-[10px]"
                  data-testid="export-blocked-by-meta-btn"
                >
                  <Download className="w-3 h-3" />
                  Descargar bloqueados por Meta
                </button>
              </>
            )}
            <span className="text-white/60 ml-auto">
              Total: {(intents || []).length || campaign.total_recipients || 0}
            </span>
          </div>
        </div>

        <Tabs defaultValue="destinatarios" className="w-full">
          <TabsList className="mx-4 mt-3 bg-white/5">
            <TabsTrigger value="destinatarios" data-testid="tab-destinatarios">Destinatarios</TabsTrigger>
            <TabsTrigger value="reporte" data-testid="tab-reporte">Reporte</TabsTrigger>
          </TabsList>
          <TabsContent value="destinatarios" className="mt-0">
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="p-6 flex items-center justify-center gap-2 text-white/50 text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando destinatarios...
            </div>
          ) : !intents?.length ? (
            <div className="p-6 text-center text-xs text-muted-foreground" data-testid="detail-empty">
              Esta campaña aún no tiene destinatarios procesados.
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="detail-intents-table">
              <thead className="bg-white/5 sticky top-0">
                <tr className="text-left text-[10px] text-white/60 uppercase tracking-wider">
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Teléfono</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Enviado</th>
                  <th className="px-3 py-2">Params usados</th>
                  <th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {intents.map(it => {
                  const style = INTENT_STATUS_STYLE[it.status] || INTENT_STATUS_STYLE.pending;
                  const err = it.error || it.last_error || '';
                  const errShort = err ? (err.length > 60 ? err.slice(0, 60) + '…' : err) : '';
                  const ERROR_TOOLTIPS = {
                    63049: 'Meta bloqueó este mensaje de marketing. El número tiene WhatsApp activo pero Meta limita la entrega por política de engagement. No es un error de ' + APP_NAME + ' — ocurre cuando el usuario recibe muchos mensajes de marketing.',
                    63024: 'Este número no tiene WhatsApp activo o usa una versión desactualizada. Se recomienda eliminar este contacto de la base de leads.',
                    63032: 'Meta descartó el mensaje como parte de un experimento interno. No es accionable.',
                    21211: 'El número de teléfono tiene un formato inválido. Verificar que tenga el prefijo de país correcto.',
                    21656: 'El formato de una variable del template es inválido. Puede ser un nombre con caracteres especiales o un campo vacío. Revisar los datos del lead.',
                  };
                  const errCode = it.error_code;
                  const tooltipText = ERROR_TOOLTIPS[errCode] || err;
                  const hasTooltip = Boolean(tooltipText);
                  return (
                    <tr key={it.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`intent-row-${it.id}`}>
                      <td className="px-3 py-2 text-white/80 max-w-[140px]">
                        <span className="block truncate" title={it.recipient_name || ''}>
                          {it.recipient_name || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-white/60 font-mono text-[11px]">{it.recipient_phone || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] ${style.bg} ${style.text}`}>
                          {style.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-white/60">
                        {(() => {
                          if (it.status === 'read' && it.read_at) return fmtSentAt(it.read_at);
                          if (it.status === 'delivered' && it.delivered_at) return fmtSentAt(it.delivered_at);
                          if (it.status === 'blocked_by_meta' && it.blocked_at) return fmtSentAt(it.blocked_at);
                          if (it.sent_at) return fmtSentAt(it.sent_at);
                          return '—';
                        })()}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1" data-testid={`intent-params-${it.id}`}>
                          {(it.params_resolved || []).map((p, i) => {
                            const full = String(p || '∅');
                            const short = full.length > 24 ? full.slice(0, 24) + '…' : full;
                            return (
                              <span
                                key={i}
                                title={full}
                                className="inline-flex px-1.5 py-0.5 rounded bg-black/30 border border-white/5 text-[10px] text-white/70 max-w-[120px] truncate"
                              >
                                {short}
                              </span>
                            );
                          })}
                          {!(it.params_resolved?.length) && <span className="text-white/40 text-[10px]">—</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2" data-testid={`intent-error-${it.id}`}>
                        {hasTooltip ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-red-400 cursor-help border-b border-dashed border-red-400/40">
                                  {errShort || (errCode ? `Error ${errCode}` : '—')}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-xs">
                                {errCode && (
                                  <p className="font-semibold mb-1">
                                    Error {errCode}
                                  </p>
                                )}
                                <p>{tooltipText}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-red-400">{errShort || '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
          </TabsContent>
          <TabsContent value="reporte" className="mt-0">
            <CampaignReportPanel campaign={campaign} intents={intents || []} counts={counts} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


const REPORT_ERROR_TOOLTIPS = {
  63049: 'Meta bloqueó el mensaje de marketing (política de engagement). No es accionable.',
  63024: 'Número sin WhatsApp activo o versión desactualizada.',
  63032: 'Meta descartó el mensaje (experimento interno). No accionable.',
  21211: 'Formato de teléfono inválido — revisar prefijo de país.',
  21656: 'Variable del template inválida — revisar datos del lead.',
};

function CampaignReportPanel({ campaign, intents, counts }) {
  const stats = campaign.stats || {};
  const total = (intents || []).length || campaign.total_recipients || 0;
  const c = {
    delivered: counts.delivered || stats.delivered || 0,
    read: counts.read || stats.read || 0,
    blocked_by_meta: counts.blocked_by_meta || stats.blocked_by_meta || 0,
    failed: counts.failed || stats.failed || 0,
    pending: counts.pending || 0,
  };

  // Donut SVG: porcentajes sobre total visible (excluye 0)
  const donutSegments = [
    { key: 'delivered', label: 'Entregados', value: c.delivered, color: '#10b981' },
    { key: 'read', label: 'Leídos', value: c.read, color: '#8b5cf6' },
    { key: 'blocked_by_meta', label: 'Bloq. Meta', value: c.blocked_by_meta, color: '#f59e0b' },
    { key: 'failed', label: 'Fallidos', value: c.failed, color: '#ef4444' },
    { key: 'pending', label: 'Pendientes', value: c.pending, color: '#6b7280' },
  ].filter(s => s.value > 0);
  const donutTotal = donutSegments.reduce((a, s) => a + s.value, 0) || 1;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const successPct = donutTotal ? Math.round(((c.delivered + c.read) / donutTotal) * 100) : 0;

  // Duración campaña
  let durationMin = null;
  if (campaign.started_at && campaign.completed_at) {
    try {
      const ms = new Date(campaign.completed_at) - new Date(campaign.started_at);
      if (ms > 0) durationMin = Math.round(ms / 60000);
    } catch { /* noop */ }
  }

  // Mensajes duplicados: agrupar por segundo de sent_at
  const sentBySecond = {};
  for (const it of intents || []) {
    if (!it.sent_at) continue;
    const key = String(it.sent_at).slice(0, 19); // YYYY-MM-DDTHH:mm:ss
    sentBySecond[key] = (sentBySecond[key] || 0) + 1;
  }
  const duplicatesCount = Object.values(sentBySecond).reduce(
    (acc, n) => acc + (n > 1 ? n - 1 : 0), 0
  );

  // Errores agrupados por error_code
  const errorBuckets = {};
  for (const it of intents || []) {
    if (it.status !== 'failed' && it.status !== 'blocked_by_meta') continue;
    const code = it.error_code || (it.status === 'blocked_by_meta' ? '63049' : 'unknown');
    if (!errorBuckets[code]) errorBuckets[code] = 0;
    errorBuckets[code] += 1;
  }
  const errorEntries = Object.entries(errorBuckets);

  const rateMsgsPerMin = campaign.rate_config?.msgs_per_minute || null;

  return (
    <div className="p-4 space-y-4" data-testid="report-panel">
      <p className="text-xs text-white/40 uppercase tracking-wider">Reporte de la campaña</p>
      {/* Métricas superiores: 5 cards */}
      <div className="grid grid-cols-5 gap-2">
        <ReportCard label="Total" value={total} color="text-white/80" />
        <ReportCard label="Entregados" value={c.delivered} color="text-emerald-400" />
        <ReportCard label="Leídos" value={c.read} color="text-violet-400" />
        <ReportCard label="Bloq. Meta" value={c.blocked_by_meta} color="text-amber-400" />
        <ReportCard label="Fallidos" value={c.failed} color="text-red-400" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Donut chart SVG */}
        <div className="bg-black/20 rounded-lg p-4 border border-white/5">
          <p className="text-[11px] text-white/50 uppercase tracking-wider mb-2">Distribución</p>
          {donutTotal > 0 ? (
            <div className="flex flex-col items-center">
              <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
                <circle cx="70" cy="70" r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="12" />
                {donutSegments.map(seg => {
                  const len = (seg.value / donutTotal) * circumference;
                  const dasharray = `${len} ${circumference - len}`;
                  const dashoffset = -offset;
                  offset += len;
                  return (
                    <circle
                      key={seg.key}
                      cx="70" cy="70" r={radius}
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="12"
                      strokeDasharray={dasharray}
                      strokeDashoffset={dashoffset}
                    />
                  );
                })}
              </svg>
              <div className="-mt-[88px] flex flex-col items-center pointer-events-none">
                <span className="text-2xl font-semibold text-white">{successPct}%</span>
                <span className="text-[10px] text-white/50">entregados</span>
              </div>
              <div className="mt-[36px] grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] w-full">
                {donutSegments.map(seg => (
                  <div key={seg.key} className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-sm" style={{ background: seg.color }} />
                    <span className="text-white/60">{seg.label}</span>
                    <span className="text-white/90 ml-auto">{seg.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-white/40 text-center py-12">Sin datos suficientes</p>
          )}
        </div>

        {/* Panel derecho: velocidad + errores */}
        <div className="space-y-3">
          <div className="bg-black/20 rounded-lg p-4 border border-white/5">
            <p className="text-[11px] text-white/50 uppercase tracking-wider mb-2">Velocidad de envío</p>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-white/60">Rate configurado</span>
                <span className="text-white/90 font-medium" data-testid="report-rate">
                  {rateMsgsPerMin ? `${rateMsgsPerMin}/min` : 'sin límite'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Mensajes duplicados</span>
                <span
                  className={`font-medium ${duplicatesCount === 0 ? 'text-emerald-400' : 'text-red-400'}`}
                  data-testid="report-duplicates"
                >
                  {duplicatesCount}
                </span>
              </div>
              {durationMin !== null && (
                <div className="flex justify-between">
                  <span className="text-white/60">Duración</span>
                  <span className="text-white/90 font-medium" data-testid="report-duration">
                    {durationMin} min
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-black/20 rounded-lg p-4 border border-white/5">
            <p className="text-[11px] text-white/50 uppercase tracking-wider mb-2">Errores detectados</p>
            {errorEntries.length === 0 ? (
              <p className="text-xs text-emerald-400/80 flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3" /> Sin errores detectados
              </p>
            ) : (
              <div className="space-y-2">
                {errorEntries.map(([code, count]) => (
                  <div key={code} className="flex items-start gap-2" data-testid={`report-error-${code}`}>
                    <Badge className="bg-red-500/10 text-red-300 text-[10px] shrink-0">{code}</Badge>
                    <p className="text-[11px] text-white/70 flex-1">
                      {REPORT_ERROR_TOOLTIPS[code] || 'Error no catalogado'}
                    </p>
                    <span className="text-[11px] text-white/90 font-medium">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReportCard({ label, value, color }) {
  return (
    <div className="bg-black/20 rounded-lg p-3 border border-white/5">
      <p className="text-[10px] text-white/50 uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-semibold mt-1 ${color}`}>{value}</p>
    </div>
  );
}


function TestSendDialog({ open, onClose, provider, template, templateParams, placeholderIndexes }) {
  const [destination, setDestination] = useState('');
  const [testValues, setTestValues] = useState({});
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setDestination('');
      setTestValues({});
      setLog(null);
      setSending(false);
    }
  }, [open]);

  if (!open) return null;

  // Valores de prueba: fixed usa value fijo; field usa input del usuario (editable).
  const resolvedTestParams = (placeholderIndexes || []).map(idx => {
    const p = (templateParams || []).find(x => x.index === idx);
    if (!p) return '';
    if (p.type === 'fixed') return String(p.value || '');
    return String(testValues[idx] || '');
  });

  const fieldParamsMissing = (templateParams || [])
    .filter(p => p.type === 'field' || p.type === 'col_excel')
    .some(p => !String(testValues[p.index] || '').trim());

  const destDigits = String(destination || '').replace(/\D/g, '');
  const canSend = !sending && destDigits.length >= 10 && !fieldParamsMissing && template;

  const handleSend = async () => {
    if (!template || !canSend) return;
    setSending(true);
    setLog(null);
    try {
      const payload = provider === 'gupshup'
        ? {
          provider: 'gupshup',
          config_id: template.gupshup_config_id || '',
          destination: destDigits,
          template_id: template.id,
          params: resolvedTestParams,
        }
        : {
          provider: 'twilio',
          destination: destDigits,
          template_id: template.id,
          params: resolvedTestParams,
        };
      const r = await api.post('/whatsapp/gupshup/test', payload, { skipAuthRedirect: true });
      const statusCode = Number(r.data?.gupshup_status_code || r.status || 0);
      setLog({
        ok: statusCode >= 200 && statusCode < 300,
        status_code: statusCode,
        response_preview: r.data?.response_preview || '',
        provider_response: r.data?.gupshup_response || {},
        curl_equivalent: r.data?.curl_equivalent || '',
        message: r.data?.message || '',
        payload_sent: r.data?.payload || payload,
        at: new Date().toLocaleTimeString(),
      });
      toast.success('Mensaje de prueba enviado');
    } catch (e) {
      const data = e.response?.data || {};
      const statusCode = Number(data.gupshup_status_code || e.response?.status || 0);
      setLog({
        ok: false,
        status_code: statusCode,
        response_preview: data.response_preview || '',
        provider_response: data.gupshup_response || {},
        curl_equivalent: data.curl_equivalent || '',
        message: data.message || data.detail || e.message || 'Error',
        payload_sent: data.payload || null,
        at: new Date().toLocaleTimeString(),
      });
      toast.error(data.message || data.detail || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  const copy = async (text, ok) => {
    try { await navigator.clipboard.writeText(String(text || '')); toast.success(ok); } catch { toast.error('No se pudo copiar'); }
  };

  const statusCls = !log ? '' : (log.ok
    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
    : 'bg-red-500/10 text-red-400 border border-red-500/20');

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 bg-card border-white/10" data-testid="test-send-dialog">
        <div className="p-4 border-b border-white/5">
          <h2 className="text-sm font-medium text-white">
            Probar template: <span className="text-orange-400">{template?.name || '—'}</span>
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Este envío NO afecta la campaña ni sus estadísticas.
          </p>
        </div>

        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Params de prueba */}
          <div className="space-y-2">
            <Label className="text-xs text-white/60">Parámetros de prueba</Label>
            {(placeholderIndexes || []).map(idx => {
              const p = (templateParams || []).find(x => x.index === idx);
              if (!p) return null;
              if (p.type === 'fixed') {
                return (
                  <div key={idx} className="flex items-center gap-2" data-testid={`test-param-fixed-${idx}`}>
                    <span className="text-[10px] text-white/50 w-24">{`{{${idx}}} — fijo`}</span>
                    <span className="inline-flex px-2 py-1 rounded bg-black/30 border border-white/5 text-[11px] text-white/80">
                      {p.value || <span className="italic text-white/40">(vacío)</span>}
                    </span>
                  </div>
                );
              }
              return (
                <div key={idx} className="flex items-center gap-2" data-testid={`test-param-field-${idx}`}>
                  <span className="text-[10px] text-white/50 w-24">{`{{${idx}}} — valor de prueba`}</span>
                  <Input
                    value={testValues[idx] || ''}
                    onChange={e => setTestValues(prev => ({ ...prev, [idx]: e.target.value }))}
                    placeholder="Ingresa valor de prueba"
                    className="bg-black/20 border-white/10 text-xs h-8 flex-1"
                    data-testid={`test-param-field-input-${idx}`}
                  />
                </div>
              );
            })}
          </div>

          {/* Destino */}
          <div>
            <Label className="text-xs text-white/60">Número de destino</Label>
            <Input
              value={destination}
              onChange={e => setDestination(e.target.value)}
              placeholder="+52155..."
              className="bg-black/20 border-white/10 text-xs mt-1 font-mono"
              data-testid="test-destination-input"
            />
            <p className="text-[9px] text-muted-foreground mt-1">
              Se enviará solo dígitos: <code className="bg-black/30 px-1 rounded">{destDigits || '—'}</code>
            </p>
          </div>

          {/* Send */}
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSend}
              disabled={!canSend}
              className="bg-emerald-600 hover:bg-emerald-500 text-xs"
              data-testid="test-send-btn"
            >
              {sending
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Enviando...</>
                : <><Send className="w-3.5 h-3.5 mr-1" /> Enviar prueba</>}
            </Button>
          </div>

          {/* Log */}
          {log && (
            <div className="space-y-2" data-testid="test-send-log">
              <div className={`px-3 py-2 rounded-lg ${statusCls} flex items-center justify-between`}>
                <span className="text-[11px]" data-testid="test-send-log-status">
                  {log.ok ? '✓ Enviado' : '✗ Error'} — HTTP {log.status_code || '—'}
                </span>
                <span className="text-[9px] text-white/40">{log.at}</span>
              </div>

              {log.message && (
                <p className={`text-[11px] ${log.ok ? 'text-white/70' : 'text-red-400'}`} data-testid="test-send-log-message">
                  {log.message}
                </p>
              )}

              {log.response_preview && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px] text-white/50">Response preview</Label>
                  </div>
                  <pre className="p-2 rounded bg-black/40 border border-white/5 text-[10px] text-white/70 whitespace-pre-wrap max-h-32 overflow-auto" data-testid="test-send-log-response">
                    {log.response_preview}
                  </pre>
                </div>
              )}

              {log.curl_equivalent && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px] text-white/50">curl equivalente</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copy(log.curl_equivalent, 'curl copiado')}
                      className="h-5 px-2 text-[9px] text-white/50 hover:text-white"
                      data-testid="test-send-copy-curl"
                    >
                      Copiar
                    </Button>
                  </div>
                  <pre className="p-2 rounded bg-black/40 border border-white/5 text-[10px] text-white/60 whitespace-pre-wrap max-h-40 overflow-auto font-mono" data-testid="test-send-log-curl">
                    {log.curl_equivalent}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// --- Reusable date/time pickers ---
// Formato visible: dd/mm/yyyy. Internamente se almacena ISO (yyyy-mm-dd).
function formatDateHuman(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
}

function DatePickerField({ label, value, onChange, testid, min, max }) {
  const hiddenRef = useRef(null);
  const openPicker = () => {
    const el = hiddenRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else el.click();
    } catch { el.click(); }
  };
  return (
    <div>
      {label && <Label className="text-[10px] text-white/40">{label}</Label>}
      <div className="relative mt-0.5">
        <Input
          readOnly
          onClick={openPicker}
          value={formatDateHuman(value)}
          placeholder="dd/mm/yyyy"
          className="bg-black/20 border-white/10 text-xs pr-9 cursor-pointer"
          data-testid={`${testid}-visible`}
        />
        <button
          type="button"
          onClick={openPicker}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
          data-testid={`${testid}-icon`}
        >
          <CalendarIcon className="w-3.5 h-3.5" />
        </button>
        <input
          ref={hiddenRef}
          type="date"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          min={min}
          max={max}
          className="sr-only absolute inset-0 opacity-0 pointer-events-none"
          data-testid={testid}
        />
      </div>
    </div>
  );
}

function DateTimePickerRow({ value, onChange }) {
  // `value` es un ISO string tipo "2026-04-22T14:30" o vacío.
  const [date, time] = (value || '').includes('T')
    ? value.split('T')
    : [value || '', ''];

  const update = (d, t) => {
    if (!d && !t) { onChange(''); return; }
    const safeTime = t || '00:00';
    onChange(`${d || ''}T${safeTime}`);
  };
  const timeRef = useRef(null);
  const openTimePicker = () => {
    const el = timeRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else el.focus();
    } catch { el.focus(); }
  };

  return (
    <div>
      <Label className="text-xs text-white/60">Fecha y hora de envío</Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <DatePickerField
          label=""
          value={date}
          onChange={v => update(v, time)}
          testid="schedule-date"
        />
        <div>
          <div className="relative">
            <input
              ref={timeRef}
              type="time"
              step="900"
              value={time}
              onChange={e => update(date, e.target.value)}
              className="w-full bg-black/20 border border-white/10 rounded-md text-xs px-3 pr-9 py-1 h-9 text-white/80 focus:outline-none focus:border-orange-500/40"
              data-testid="schedule-time"
            />
            <button
              type="button"
              onClick={openTimePicker}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
              data-testid="schedule-time-icon"
            >
              <Clock className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      <p className="text-[9px] text-muted-foreground mt-1" data-testid="schedule-preview">
        {date ? `${formatDateHuman(date)}${time ? ` a las ${time}` : ''}` : 'Selecciona fecha y hora'}
      </p>
    </div>
  );
}
